import { FC, useEffect } from "react";
import { CatalogPage } from "@/lib/pages/CatalogPage";
import { CatalogService } from "@/lib/services/CatalogService";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import {
  CatalogQuery,
  ICatalogObject,
  ICatalogPage,
  IFirstRowItem,
  PredefinedCatalogQuery,
} from "@/lib/types/catalogPage";
import { GetServerSideProps } from "next";
import Head from "next/head";
import { i18n, useTranslation } from "next-i18next";
import { BASE_FRONT_URL, isDevelopment } from "@/lib/api/global";
import { firstRowItems } from "@/lib/ui/blocks/page-specific/Catalog/CatalogFilter/constants";
import { getFilteredProperties } from "@/lib/hooks/useFilteredProperties";
import { LCPhuketDistrict, LCSamuiDistrict } from "../../../lib/types/complexPage";
import { useTranslateCatalog } from "../../../lib/hooks/useTranslateCatalog";
import { useCatalogActions } from "../../../lib/store/slices/catalog";

export interface ICatalogProps {
  data: ICatalogObject[];
  filteredData: ICatalogObject[];
  firstRowItems: IFirstRowItem[];
  url: string;
  params: PredefinedCatalogQuery;
}

export const Catalog: FC<ICatalogProps> = ({
  data = [],
  firstRowItems = [],
  params,
  url,
  filteredData = [],
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation("index", { keyPrefix: "meta" });
  const { title, description, h1 } = useTranslateCatalog({
    dealType: "sale",
    ...params,
    properties: data,
  });
  const { setParamState } = useCatalogActions();
  useEffect(() => {
    setParamState({
      ...(params.propertyType !== "realty" && params.propertyType !== null
        ? {
            propertyType:
              params.propertyType === "flat" ? "apartment" : params.propertyType,
          }
        : {}),

      ...(params.phuketDistrict
        ? {
            phuketDistrict:
              params.phuketDistrict === "bang-tao"
                ? ["bang-tao", "lagoon", "layan"]
                : params.phuketDistrict === "karon"
                  ? ["kata-noi", "kata", "karon"]
                  : params.phuketDistrict === "rawai"
                    ? ["rawai", "nai-harn"]
                    : (params.phuketDistrict as LCPhuketDistrict),
          }
        : {}),
      ...(params.samuiDistrict
        ? {
            samuiDistrict: params.samuiDistrict as LCSamuiDistrict,
          }
        : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);
  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:keywords" content={t("catalog.keywords")} />
        <meta property="og:image" content={"/images/og.png"} />
        <meta property="og:type" content="website" />
        <meta property="og:image:width" content="510" />
        <meta property="og:site_name" content="Villacarte" />
        <meta property="og:url" content={BASE_FRONT_URL} />
        {params.page && <meta name="robots" content="noindex, nofollow" />}
        <link
          rel="canonical"
          href={`${BASE_FRONT_URL}/${language}${url.replaceAll(/&page=\d+|\?page=\d+/gi, "")}`}
        ></link>
        <meta
          name="cc:attributionURL"
          content={`${BASE_FRONT_URL}/${language}${url.replaceAll(/&page=\d+|\?page=\d+/gi, "")}`}
        />
        <link rel="alternate" href={`${BASE_FRONT_URL}/en${url}`} hrefLang="en" />
        <link rel="alternate" href={`${BASE_FRONT_URL}/ru${url}`} hrefLang="ru" />
        <link rel="alternate" href={`${BASE_FRONT_URL}/en${url}`} hrefLang="x-default" />
      </Head>
      <CatalogPage
        data={data}
        filteredData={filteredData}
        firstRowItems={firstRowItems}
        title={h1}
      />
    </>
  );
};
//@ts-expect-error
export const getServerSideProps: GetServerSideProps<ICatalogProps> = async ({
  locale,
  params,
  resolvedUrl,
  res,
  previewData,
  query,
}) => {
  if (
    params &&
    params.predefinedParams &&
    Array.isArray(params.predefinedParams) &&
    +params.predefinedParams.length > 3
  ) {
    return {
      redirect: { destination: "/sale/catalog/" },
      props: {
        ...(await serverSideTranslations(locale ?? "en", ["index"])),
        data: [],
        filteredData: [],
        firstRowItems: [],
        params: {},
        url: resolvedUrl,
      },
    };
  }
  const data = (previewData as ICatalogPage) || (await CatalogService.getCatalogData());

  const { propertyType, island, samuiDistrict, phuketDistrict, page } =
    CatalogService.extractPropertiesFromParams(params, query as CatalogQuery);
  const objects: ICatalogObject[] = data.saleObjects;
  const filteredFirstRowItems = JSON.parse(
    JSON.stringify(
      firstRowItems.filter(
        (item) => getFilteredProperties(objects, item.query, "usd").length > 0,
      ),
    ),
  );
  const filteredProperties = getFilteredProperties(
    objects,
    {
      propertyType: propertyType || undefined,
      island,
      samuiDistrict: samuiDistrict || undefined,
      phuketDistrict: phuketDistrict || undefined,
    },
    "usd",
  );

  if (isDevelopment) {
    await i18n?.reloadResources();
  }
  const composedParams = {
    propertyType,
    island,
    phuketDistrict,
    samuiDistrict,
  };
  const url = resolvedUrl;

  res.setHeader("Cache-Control", "public, s-maxage=300, stale-while-revalidate=600");
  return {
    props: {
      ...(await serverSideTranslations(locale ?? "en", ["index"])),
      data: objects,
      filteredData: filteredProperties,
      params: composedParams,
      url,
      firstRowItems: filteredFirstRowItems,
      headerTheme: "light",
    },
  };
};
export default Catalog;
